import { Helmet } from "react-helmet-async";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";

function normalizeText(value) {
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeVehicle(value) {
  // make vehicle matching more robust: ignore spaces and hyphens
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/-/g, "");
}

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function lowerBound(arr, t) {
  // first idx where arr[idx].t >= t
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function upperBound(arr, t) {
  // first idx where arr[idx].t > t
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function StockReport() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState("en");
  const [isUrdu, setIsUrdu] = useState(false);

  const [bills, setBills] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === "ur" ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem("companyToken");
        if (!token) {
          navigate("/company-login");
          return;
        }
        const response = await api.get("/company/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompany(response.data.company);
        setCompanyLanguage(response.data.company?.language || "en");
        setIsUrdu(response.data.company?.language === "ur");
      } catch (error) {
        console.error("Error fetching company:", error);
        navigate("/company-login");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("companyToken");
        if (!token) {
          toast.error(companyLanguage === "ur" ? "براہ کرم لاگ ان کریں" : "Please login");
          return;
        }

        const [billsRes, salesRes] = await Promise.all([
          api.get("/bill/company/my-bills", { headers: { Authorization: `Bearer ${token}` } }),
          api.get("/sale/company/all", { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setBills(billsRes.data.bills || []);
        setSales(salesRes.data.sales || []);
      } catch (error) {
        console.error("Error fetching stock report data:", error);
        toast.error(isUrdu ? "ڈیٹا لانے میں ناکام" : "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    if (company) fetchData();
  }, [company, companyLanguage, isUrdu]);

  const rows = useMemo(() => {
    // Prepare sale item events: key = supplier|vehicle|item
    const eventsByKey = new Map();
    // Also index by sale receipt number so we can attach sales directly to the bill row when numbers match
    const eventsByKeyAndSaleNo = new Map();

    for (const sale of sales || []) {
      const supplier = normalizeText(sale?.supplierNameUrdu || sale?.supplierName);
      const vehicle = normalizeVehicle(sale?.supplierVehicleNumber);
      const saleNo = normalizeText(sale?.serialNumber);
      const saleDate = sale?.date ? new Date(sale.date).getTime() : 0;

      const items = Array.isArray(sale?.items) ? sale.items : [];
      for (const it of items) {
        const itemName = normalizeText(it?.descriptionUrdu || it?.description);
        if (!supplier || !itemName) continue;

        const key = `${supplier}||${vehicle}||${itemName}`;
        const e = {
          t: saleDate,
          qty: toNumber(it?.quantity),
          amount: toNumber(it?.lineTotal),
          saleNo,
        };
        if (!eventsByKey.has(key)) eventsByKey.set(key, []);
        eventsByKey.get(key).push(e);

        if (saleNo) {
          const keyNo = `${key}||${saleNo}`;
          if (!eventsByKeyAndSaleNo.has(keyNo)) eventsByKeyAndSaleNo.set(keyNo, []);
          eventsByKeyAndSaleNo.get(keyNo).push(e);
        }
      }
    }

    // Sort events and create prefix sums
    const eventDataByKey = new Map();
    for (const [key, arr] of eventsByKey.entries()) {
      arr.sort((a, b) => a.t - b.t);
      const prefixQty = new Array(arr.length + 1).fill(0);
      const prefixAmt = new Array(arr.length + 1).fill(0);
      for (let i = 0; i < arr.length; i++) {
        prefixQty[i + 1] = prefixQty[i] + (arr[i].qty || 0);
        prefixAmt[i + 1] = prefixAmt[i] + (arr[i].amount || 0);
      }
      eventDataByKey.set(key, { arr, prefixQty, prefixAmt });
    }

    const eventDataByKeyAndSaleNo = new Map();
    for (const [key, arr] of eventsByKeyAndSaleNo.entries()) {
      arr.sort((a, b) => a.t - b.t);
      const prefixQty = new Array(arr.length + 1).fill(0);
      const prefixAmt = new Array(arr.length + 1).fill(0);
      for (let i = 0; i < arr.length; i++) {
        prefixQty[i + 1] = prefixQty[i] + (arr[i].qty || 0);
        prefixAmt[i + 1] = prefixAmt[i] + (arr[i].amount || 0);
      }
      eventDataByKeyAndSaleNo.set(key, { arr, prefixQty, prefixAmt });
    }

    // Group bills by key so we can define a window per bill (until next bill with same key)
    const billsList = (bills || [])
      .filter((b) => (b?.status || "completed") === "completed")
      .slice()
      .sort((a, b) => new Date(a?.date || 0).getTime() - new Date(b?.date || 0).getTime());

    const billsByKey = new Map();
    for (const bill of billsList) {
      const supplier = normalizeText(bill?.customerNameUrdu || bill?.customerName);
      const vehicle = normalizeVehicle(bill?.expenses?.ActualVehicle || bill?.expenses?.Vehicle);
      const itemName = normalizeText(bill?.expenses?.ItemName);
      if (!supplier || !itemName) continue;

      const key = `${supplier}||${vehicle}||${itemName}`;
      if (!billsByKey.has(key)) billsByKey.set(key, []);
      billsByKey.get(key).push(bill);
    }

    const out = [];

    for (const [key, keyBills] of billsByKey.entries()) {
      const data = eventDataByKey.get(key);
      for (let i = 0; i < keyBills.length; i++) {
        const bill = keyBills[i];
        const nextBill = keyBills[i + 1];
        const startT = bill?.date ? new Date(bill.date).getTime() : 0;
        const endT = nextBill?.date ? new Date(nextBill.date).getTime() - 1 : Number.POSITIVE_INFINITY;

        let soldQty = 0;
        let soldAmt = 0;
        // 1) Preferred: if Sale receipt number matches Bill number, attach those sales to THIS bill row
        const billNoNorm = normalizeText(bill?.serialNumber || bill?.voucherNumber);
        if (billNoNorm) {
          const dataNo = eventDataByKeyAndSaleNo.get(`${key}||${billNoNorm}`);
          if (dataNo && dataNo.arr.length) {
            soldQty = dataNo.prefixQty[dataNo.arr.length] - dataNo.prefixQty[0];
            soldAmt = dataNo.prefixAmt[dataNo.arr.length] - dataNo.prefixAmt[0];
          } else if (data && data.arr.length) {
            // 2) Fallback: assign by date window (between this bill date and next bill date for same supplier+vehicle+item)
            const s = lowerBound(data.arr, startT);
            const e = endT === Number.POSITIVE_INFINITY ? data.arr.length : upperBound(data.arr, endT);
            soldQty = data.prefixQty[e] - data.prefixQty[s];
            soldAmt = data.prefixAmt[e] - data.prefixAmt[s];
          }
        } else if (data && data.arr.length) {
          const s = lowerBound(data.arr, startT);
          const e = endT === Number.POSITIVE_INFINITY ? data.arr.length : upperBound(data.arr, endT);
          soldQty = data.prefixQty[e] - data.prefixQty[s];
          soldAmt = data.prefixAmt[e] - data.prefixAmt[s];
        }

        const billQty = toNumber(bill?.expenses?.ItemQuantity);
        const remainingQty = billQty - soldQty;

        out.push({
          billId: bill?._id,
          billNumber: bill?.serialNumber || bill?.voucherNumber || "",
          supplierName: bill?.customerNameUrdu || bill?.customerName || "",
          gardiNumber: bill?.expenses?.ActualVehicle || "",
          quantity: billQty,
          stockAmount: toNumber(bill?.rawSale || bill?.sales?.rawSale || bill?.sales?.["Raw Sale"] || 0),
          itemName: bill?.expenses?.ItemName || "",
          saleQuantity: soldQty,
          saleAmount: soldAmt,
          remainingQuantity: remainingQty,
          date: bill?.date ? new Date(bill.date) : null,
        });
      }
    }

    // Sort by bill date desc for UI
    out.sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
    return out;
  }, [bills, sales]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-3"></div>
          <p className="text-gray-600">{isUrdu ? "لوڈ ہو رہا ہے..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {company && (
        <DashboardHeader company={company} companyLanguage={companyLanguage} onLogout={handleLogout} />
      )}

      <Helmet>
        <title>{companyLanguage === "ur" ? "اسٹاک رپورٹ" : "Stock Report"}</title>
      </Helmet>

      <div className="max-w-7xl mx-auto pt-[6px] pb-8 px-4" dir={companyLanguage === "ur" ? "rtl" : "ltr"}>
        <div className="bg-white rounded-xl shadow border border-gray-200 p-3 mb-3">
          <div
            className={`flex flex-col md:justify-between md:items-center gap-2 ${
              companyLanguage === "ur" ? "md:flex-row-reverse text-right" : "md:flex-row text-left"
            }`}
          >
            <div>
              <h1 className={`text-lg font-bold text-gray-900 ${companyLanguage === "ur" ? "font-urdu" : ""}`}>
                {companyLanguage === "ur" ? "اسٹاک رپورٹ" : "Stock Report"}
              </h1>
              <p className={`text-xs text-gray-600 ${companyLanguage === "ur" ? "font-urdu" : ""}`}>
                {companyLanguage === "ur" ? "بلز اور سیلز کے مطابق باقی اسٹاک" : "Remaining stock based on Bills and Sales"}
              </p>
            </div>
            <div className={`text-xs text-gray-700 ${companyLanguage === "ur" ? "text-left" : "text-right"}`}>
              <span className={`font-semibold ${companyLanguage === "ur" ? "font-urdu" : ""}`}>
                {companyLanguage === "ur" ? "ریکارڈز:" : "Records:"}
              </span>{" "}
              {loading ? (companyLanguage === "ur" ? "لوڈنگ..." : "Loading...") : rows.length}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <div className="overflow-auto">
            <table className="min-w-[1100px] w-full text-sm">
              <thead className="bg-green-700 text-white sticky top-0">
                <tr className={`${companyLanguage === "ur" ? "font-urdu" : ""}`}>
                  <th className={`px-3 py-2 ${companyLanguage === "ur" ? "text-right" : "text-left"}`}>{companyLanguage === "ur" ? "بل نمبر" : "Bill #"}</th>
                  <th className={`px-3 py-2 ${companyLanguage === "ur" ? "text-right" : "text-left"}`}>{companyLanguage === "ur" ? "سپلائر" : "Supplier"}</th>
                  <th className={`px-3 py-2 ${companyLanguage === "ur" ? "text-right" : "text-left"}`}>{companyLanguage === "ur" ? "گاڑی نمبر" : "Gardi #"}</th>
                  <th className="px-3 py-2 text-right">{companyLanguage === "ur" ? "مقدار" : "Qty"}</th>
                  <th className="px-3 py-2 text-right">{companyLanguage === "ur" ? "اسٹاک رقم" : "Stock Amount"}</th>
                  <th className={`px-3 py-2 ${companyLanguage === "ur" ? "text-right" : "text-left"}`}>{companyLanguage === "ur" ? "چیز" : "Item"}</th>
                  <th className="px-3 py-2 text-right">{companyLanguage === "ur" ? "سیل مقدار" : "Sale Qty"}</th>
                  <th className="px-3 py-2 text-right">{companyLanguage === "ur" ? "سیل رقم" : "Sales Amount"}</th>
                  <th className="px-3 py-2 text-right">{companyLanguage === "ur" ? "باقی مقدار" : "Remaining"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((r) => (
                  <tr key={r.billId} className="hover:bg-green-50">
                    <td className={`px-3 py-2 ${companyLanguage === "ur" ? "text-right" : "text-left"}`}>
                      <button
                        className="text-blue-700 hover:underline font-semibold"
                        onClick={() => navigate(`/bill/view/${r.billId}`)}
                      >
                        {r.billNumber || "—"}
                      </button>
                    </td>
                    <td className={`px-3 py-2 ${companyLanguage === "ur" ? "font-urdu text-right" : "text-left"}`}>{r.supplierName || "—"}</td>
                    <td className={`px-3 py-2 ${companyLanguage === "ur" ? "text-right" : "text-left"}`}>{r.gardiNumber || "—"}</td>
                    <td className="px-3 py-2 text-right">{toNumber(r.quantity).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{toNumber(r.stockAmount).toFixed(2)}</td>
                    <td className={`px-3 py-2 ${companyLanguage === "ur" ? "font-urdu text-right" : "text-left"}`}>{r.itemName || "—"}</td>
                    <td className="px-3 py-2 text-right">{toNumber(r.saleQuantity).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{toNumber(r.saleAmount).toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right font-bold ${r.remainingQuantity < 0 ? "text-red-600" : "text-green-700"}`}>
                      {toNumber(r.remainingQuantity).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className={`px-3 py-6 text-center text-gray-600 ${companyLanguage === "ur" ? "font-urdu" : ""}`}>
                      {companyLanguage === "ur" ? "کوئی ریکارڈ نہیں ملا" : "No records found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StockReport;


